import {
  ChartColumn,
  Building2,
  CalendarDays,
  CircleDot,
  Clock,
  Flag,
  House,
  Landmark,
  Magnet,
  MapPin,
  Megaphone,
  PhoneOutgoing,
  Tag,
  Target,
  UsersRound,
  type LucideIcon,
} from "lucide-react";

import type { Permission } from "@/app/session/permissions";
import type { MessageId } from "@/shared/i18n/messages";

export type NavGroup = "overview" | "clients" | "sales" | "inventory" | "team" | "setup";

export interface NavEntry {
  /** Stable id: the React key, the command palette value and the `g` shortcut target. */
  id: string;
  to: string;
  labelId: MessageId;
  icon: LucideIcon;
  /** Shown only to people with this permission, or any of these (see useCan). */
  permission?: Permission | readonly Permission[];
  /** Active only on the exact path. */
  end?: boolean;
  group: NavGroup;
}

/** Sidebar sections, in order. The overview group has no heading. */
export const NAV_GROUPS: readonly { id: NavGroup; labelId?: MessageId }[] = [
  { id: "overview" },
  { id: "clients", labelId: "nav.group.clients" },
  { id: "sales", labelId: "nav.group.sales" },
  { id: "inventory", labelId: "nav.group.inventory" },
  { id: "team", labelId: "nav.group.team" },
  { id: "setup", labelId: "nav.group.setup" },
];

/* Pipeline, Requests and Resale left the sidebar when the lead became the whole journey, and
   Claims and Tips went with them; their pages still open from old links. Permission keys are exactly the ones the API's middleware checks for each list. */
export const NAV_ENTRIES: readonly NavEntry[] = [
  { id: "home", to: "/", labelId: "nav.home", icon: House, end: true, group: "overview" },
  {
    id: "calendar",
    to: "/calendar",
    labelId: "nav.calendar",
    icon: CalendarDays,
    permission: "calendar-task.view",
    group: "overview",
  },
  {
    // Everyone has a report: their own, their team's, or the company's.
    id: "reports",
    to: "/reports",
    labelId: "nav.reports",
    icon: ChartColumn,
    group: "overview",
  },
  {
    id: "leads",
    to: "/leads",
    labelId: "nav.leads",
    icon: Magnet,
    permission: "lead.view",
    group: "clients",
  },
  {
    // The pool of canceled and rotated-out leads, pulled back to agents (leaders and admins).
    id: "cold-calls",
    to: "/cold-calls",
    labelId: "nav.coldCalls",
    icon: PhoneOutgoing,
    permission: "lead.pool",
    group: "clients",
  },
  {
    id: "units",
    to: "/units",
    labelId: "nav.units",
    icon: Building2,
    permission: "inventory.view",
    group: "inventory",
  },
  {
    id: "team",
    to: "/team",
    labelId: "nav.team",
    icon: UsersRound,
    permission: ["user.view", "role.view"],
    group: "team",
  },
  {
    // Everyone has a target to see: their own, their team's, or the company's.
    id: "targets",
    to: "/targets",
    labelId: "nav.targets",
    icon: Target,
    group: "team",
  },
  {
    id: "catalog",
    to: "/setup/catalog",
    labelId: "nav.catalog",
    icon: Landmark,
    permission: ["developer.create", "project.create"],
    group: "setup",
  },
  {
    id: "statuses",
    to: "/setup/statuses",
    labelId: "nav.statuses",
    icon: CircleDot,
    permission: ["status.create", "status.edit", "status.delete"],
    group: "setup",
  },
  {
    id: "working-hours",
    to: "/setup/working-hours",
    labelId: "nav.workingHours",
    icon: Clock,
    permission: "settings.edit",
    group: "setup",
  },
  {
    id: "unit-tags",
    to: "/setup/tags",
    labelId: "nav.unitTags",
    icon: Tag,
    permission: ["unit-tag.create", "unit-tag.edit", "unit-tag.delete"],
    group: "setup",
  },
  {
    id: "sources",
    to: "/setup/sources",
    labelId: "nav.sources",
    icon: Megaphone,
    permission: "source.create",
    group: "setup",
  },
  {
    id: "campaigns",
    to: "/setup/campaigns",
    labelId: "nav.campaigns",
    icon: Flag,
    permission: "source.create",
    group: "setup",
  },
  {
    id: "areas",
    to: "/setup/areas",
    labelId: "nav.areas",
    icon: MapPin,
    permission: "source.create",
    group: "setup",
  },
];

/** `g` then a key jumps to a section (see useGoShortcuts). */
export const GO_KEYS: Readonly<Record<string, string>> = {
  h: "/",
  c: "/calendar",
  l: "/leads",
  r: "/reports",
  u: "/units",
};
